import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Settings, HelpCircle, Bell, LogOut, CalendarCheck2 } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { SearchBox } from "./SearchBox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { AppearanceMenu, SuiteAppLauncher, SuiteAppTile } from "@athavita/ui-theme";
import { getInitials } from "@/lib/utils";
import { updatePreferences, usePreferences } from "@/lib/preferences";
import { AppBarButton } from "./AppBarButton";

interface TopBarProps {
    searchInputRef?: React.RefObject<HTMLInputElement>;
    onToggleAi?: () => void;
    onOpenPalette?: () => void;
}

/** The My Day pane arrives in Phase C (Doc/MAIL_UI_REDESIGN.md); its on/off state is already saved. */
const MY_DAY_PANE_AVAILABLE = false;

// Outlook-on-the-Web layout:
//   [waffle] [brand]  ···  [search centered]  ···  [My Day AI bell appearance help settings avatar]
// Colours follow the accent palette and the top-bar style (Accent / Light / Dark).
export function TopBar({ searchInputRef, onToggleAi, onOpenPalette }: TopBarProps) {
    const auth = useAuth();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const myDay = usePreferences((p) => p.layout.myDay);

    const displayName =
        auth.user?.profile.name
        ?? auth.user?.profile.preferred_username
        ?? auth.user?.profile.email
        ?? "you";

    return (
        <header className="mail-appbar flex h-12 shrink-0 items-center gap-2 px-2 sm:gap-3 sm:px-3">
            {/* Launcher, tile and Appearance are the suite's shared ones (@athavita/ui-theme suite-shell). */}
            <SuiteAppLauncher current="mail" />
            <span className="flex shrink-0 items-center gap-2">
                <SuiteAppTile app="mail" size={24} />
                <span className="mail-appbar-brand hidden whitespace-nowrap text-[15px] font-semibold sm:inline">Athavita Mail</span>
            </span>

            <div className="flex min-w-0 flex-1 justify-center px-1 sm:px-4">
                <SearchBox searchInputRef={searchInputRef} onOpenPalette={onOpenPalette} />
            </div>

            <div className="flex items-center gap-0.5">
                {MY_DAY_PANE_AVAILABLE ? (
                    <AppBarButton
                        label="My Day"
                        pressed={myDay}
                        onClick={() => updatePreferences({ layout: { myDay: !myDay } })}
                    >
                        <CalendarCheck2 className="h-5 w-5" aria-hidden />
                    </AppBarButton>
                ) : null}
                <AppBarButton label="Athavita AI" onClick={onToggleAi}>
                    <Sparkles className="h-5 w-5" aria-hidden />
                </AppBarButton>
                <AppBarButton
                    label="Notification settings"
                    className="hidden sm:inline-flex"
                    onClick={() => navigate("/settings/notifications")}
                >
                    <Bell className="h-5 w-5" aria-hidden />
                </AppBarButton>
                {/* Mode (light / dark / system) lives in the Appearance menu with the accent and top bar —
                    no separate dark-mode button (user feedback 2026-09-15). Suite-wide unless Mail has
                    its own look. Also in Ctrl+K and Settings. */}
                <AppearanceMenu app="mail" />
                <AppBarButton
                    label="Keyboard shortcuts & help"
                    className="hidden md:inline-flex"
                    onClick={() => navigate("/settings/shortcuts")}
                >
                    <HelpCircle className="h-5 w-5" aria-hidden />
                </AppBarButton>
                <AppBarButton label="Settings" onClick={() => navigate("/settings")}>
                    <Settings className="h-5 w-5" aria-hidden />
                </AppBarButton>
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            className="mail-appbar-btn ml-1 rounded-full"
                            aria-label={`Account menu for ${displayName}`}
                        >
                            <Avatar className="h-8 w-8">
                                {/* A tinted disc on any top-bar style, as in the mock-up. */}
                                <AvatarFallback className="bg-[color-mix(in_srgb,hsl(var(--primary))_14%,white)] text-xs font-semibold text-[color-mix(in_srgb,hsl(var(--primary))_70%,black)]">
                                    {getInitials(displayName)}
                                </AvatarFallback>
                            </Avatar>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="flex flex-col gap-0.5">
                            <span>{displayName}</span>
                            {auth.user?.profile.email ? (
                                <span className="text-xs font-normal text-muted-foreground">
                                    {auth.user.profile.email}
                                </span>
                            ) : null}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => void auth.signoutRedirect()}
                            className="text-destructive focus:text-destructive"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
